import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { adminApi } from '../../services/api';
import { setSelectedReferral, setReferralTypes, setReferralStatuses } from '../redux/slices/referral-management/referralSlice';
import SidebarPanel from '../admin/SidebarPanel';
import ActivityTimeline from '../shared/ActivityTimeline';
import AlertMessage from '../shared/alertMessage';
import AddReferralItemForm from './AddReferralItemForm';
import TaskActivityTimeline from './TaskActivityTimeline';
import PhoneNumberManager from '../sales/shared/PhoneNumberManager';
import RetellCallViewer from '../sales/RetellCallViewer';
import CallQueuePanel from '../sales/CallQueuePanel';
import { toE164, isValidE164 } from '../../utils/phoneUtils';
import { useIsAnkrAdmin } from '../../context/AuthContext';
import debounce from 'lodash/debounce';

const ReferralDetailPanel = ({ referral, onClose, onUpdate, onNavigate, currentIndex, totalEvents, initialTab = 'overview' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAnkrAdmin = useIsAnkrAdmin();
  const referralTypes = useSelector((state) => state.referral.referralTypes);
  const referralStatuses = useSelector((state) => state.referral.referralStatuses);

  const [fullReferral, setFullReferral] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });
  const [activitiesData, setActivitiesData] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditingReferral, setIsEditingReferral] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isEditingPatient, setIsEditingPatient] = useState(false);
  const [patientEditFormData, setPatientEditFormData] = useState({});
  const [editingItemId, setEditingItemId] = useState(null);
  const [itemEditData, setItemEditData] = useState({});
  const [originalItemData, setOriginalItemData] = useState({});
  const [expandedTaskActivities, setExpandedTaskActivities] = useState({});
  const [taskActivityRefreshTrigger, setTaskActivityRefreshTrigger] = useState({});
  const [healthcareUsers, setHealthcareUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isEditingEhr, setIsEditingEhr] = useState(false);
  const [ehrCompletionReason, setEhrCompletionReason] = useState('');

  // Edit form - Facility search state
  const [editFacilitySearchTerm, setEditFacilitySearchTerm] = useState('');
  const [editFacilitySearchResults, setEditFacilitySearchResults] = useState([]);
  const [editFacilitySearching, setEditFacilitySearching] = useState(false);
  const [editSelectedFacility, setEditSelectedFacility] = useState(null);

  // Edit form - Contact search state
  const [editContactSearchTerm, setEditContactSearchTerm] = useState('');
  const [editContactSearchResults, setEditContactSearchResults] = useState([]);
  const [editContactSearching, setEditContactSearching] = useState(false);
  const [editSelectedContact, setEditSelectedContact] = useState(null);

  // Navigation state
  const [isNavigating, setIsNavigating] = useState(false);
  const navigateTimerRef = useRef(null);

  // Handle navigation between referrals
  const handleNavigate = useCallback((direction) => {
    if (!onNavigate || isNavigating) return;

    // Clear any existing timer
    if (navigateTimerRef.current) {
      clearTimeout(navigateTimerRef.current);
    }

    // Debounce navigation to prevent rapid clicks
    navigateTimerRef.current = setTimeout(() => {
      setIsNavigating(true);

      // Auto-close edit mode if open
      if (isEditingReferral) {
        setIsEditingReferral(false);
        setEditFormData({});
      }
      if (isEditingPatient) {
        setIsEditingPatient(false);
        setPatientEditFormData({});
      }
      if (editingItemId) {
        setEditingItemId(null);
        setItemEditData({});
        setOriginalItemData({});
      }

      // Trigger navigation
      onNavigate(direction);

      // Reset navigation state after a short delay
      setTimeout(() => {
        setIsNavigating(false);
      }, 300);
    }, 400);
  }, [onNavigate, isNavigating, isEditingReferral, isEditingPatient, editingItemId]);

  // Helper function to get MRN info from patient's ehr_profiles
  const getMrnInfo = (patient) => {
    if (!patient) return { display: 'N/A', editable: false, value: '', ehrProfileId: null };

    const ehrProfiles = patient.ehr_profiles || [];

    if (ehrProfiles.length === 0) {
      // No EHR profiles - show legacy patient_id (read-only)
      return {
        display: patient.patient_id || 'N/A',
        editable: false,
        value: patient.patient_id || '',
        ehrProfileId: null,
        mode: 'legacy'
      };
    } else if (ehrProfiles.length === 1) {
      // Single EHR profile - show MRN from ehr_identifiers (editable)
      const profile = ehrProfiles[0];
      const mrn = profile.ehr_identifiers?.mrn || '';
      return {
        display: mrn || 'Not set',
        editable: true,
        value: mrn,
        ehrProfileId: profile.id,
        mode: 'single',
        healthSystemName: profile.health_system_name
      };
    } else {
      // Multiple EHR profiles - show "Multiple available" (read-only)
      return {
        display: 'Multiple available',
        editable: false,
        value: '',
        ehrProfileId: null,
        mode: 'multiple',
        count: ehrProfiles.length
      };
    }
  };

  // Cleanup navigation timer on unmount
  useEffect(() => {
    return () => {
      if (navigateTimerRef.current) {
        clearTimeout(navigateTimerRef.current);
      }
    };
  }, []);

  // Calling state (from RetellCallPanel)
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [fromNumber, setFromNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneExtension, setPhoneExtension] = useState('');
  const [prospectName, setProspectName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [loadingCall, setLoadingCall] = useState(false);

  // Call state
  const [callId, setCallId] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [showCallSetup, setShowCallSetup] = useState(true);

  // Call viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState(null);

  // Call queue panel state
  const [queuePanelOpen, setQueuePanelOpen] = useState(false);

  // Outreach history
  const [outreachHistory, setOutreachHistory] = useState([]);
  const [loadingOutreachHistory, setLoadingOutreachHistory] = useState(false);
  const [refreshingCallId, setRefreshingCallId] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [autoRefreshCallQueue, setAutoRefreshCallQueue] = useState(false);
  const [expandedActivities, setExpandedActivities] = useState({});
  const [activityDetails, setActivityDetails] = useState({});
  const [loadingActivityDetails, setLoadingActivityDetails] = useState({});

  // Item-specific calling state
  const [callingItemId, setCallingItemId] = useState(null); // Which item is showing call UI
  const [itemAdditionalContext, setItemAdditionalContext] = useState(''); // Item-specific context
  const [selectedContactForItem, setSelectedContactForItem] = useState(null); // Selected contact
  const [itemPhoneNumber, setItemPhoneNumber] = useState(''); // Phone for item call
  const [itemPhoneExtension, setItemPhoneExtension] = useState(''); // Extension for item call

  // Fetch full referral details
  useEffect(() => {
    const fetchDetails = async () => {
      if (!referral?.id) return;

      setLoading(true);
      try {
        const response = await adminApi.get(`admin/referrals/${referral.id}/`);
        if (response.data?.referral) {
          setFullReferral(response.data.referral);
          dispatch(setSelectedReferral(response.data.referral));
        }
      } catch (err) {
        console.error('Failed to fetch referral details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [referral?.id, dispatch]);

  // Fetch referral types and statuses scoped to THIS referral's health system/organization
  useEffect(() => {
    const fetchConfiguration = async () => {
      if (!fullReferral) return;

      const healthSystemId = fullReferral.health_system?.id;
      const organizationId = fullReferral.organization?.id;

      try {
        const params = { is_active: 'true' };

        // Use the referral's health_system_id or organization_id for scoping
        if (healthSystemId) {
          params.health_system_id = healthSystemId;
        } else if (organizationId) {
          params.organization_id = organizationId;
        }

        const [typesResponse, statusesResponse] = await Promise.all([
          adminApi.get('admin/referral-types/', { params }),
          adminApi.get('admin/referral-statuses/', { params }),
        ]);

        if (typesResponse.data?.referral_types) {
          dispatch(setReferralTypes(typesResponse.data.referral_types));
        }
        if (statusesResponse.data?.referral_statuses) {
          dispatch(setReferralStatuses(statusesResponse.data.referral_statuses));
        }
      } catch (err) {
        console.error('Failed to fetch referral configuration:', err);
      }
    };

    fetchConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullReferral?.health_system?.id, fullReferral?.organization?.id, dispatch]);

  // Fetch activities
  const fetchActivities = async () => {
    if (!fullReferral?.id) return;

    setActivitiesLoading(true);
    try {
      const response = await adminApi.get(`admin/referrals/${fullReferral.id}/activities/`);
      setActivitiesData(response.data.results || []);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  // Only fetch activities when the Activity tab is opened
  useEffect(() => {
    if (fullReferral?.id && activeTab === 'activity') {
      fetchActivities();
    }
  }, [fullReferral?.id, activeTab]);

  // Update activeTab when initialTab prop changes
  useEffect(() => {
    const validTabs = ['overview', 'tasks', 'activity'];
    if (initialTab && validTabs.includes(initialTab)) {
      setActiveTab(initialTab);
    } else if (initialTab && !validTabs.includes(initialTab)) {
      setActiveTab('overview');
    }
  }, [initialTab]);

  // Auto-dismiss success alerts after 5 seconds
  useEffect(() => {
    if (alertState.show && alertState.type === 'success') {
      const timer = setTimeout(() => {
        setAlertState(prev => ({ ...prev, show: false }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alertState.show, alertState.type]);

  // Handle successful item creation
  const handleItemAdded = async (newItem) => {
    setAlertState({
      show: true,
      type: 'success',
      title: 'Item Added',
      message: `Successfully added ${newItem.referral_type?.name || 'referral item'}`
    });

    // Refresh referral details to get updated items
    try {
      const response = await adminApi.get(`admin/referrals/${fullReferral.id}/`);
      if (response.data?.referral) {
        setFullReferral(response.data.referral);
        dispatch(setSelectedReferral(response.data.referral));

        // Notify parent to refresh list
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (err) {
      console.error('Failed to refresh referral:', err);
    }

    // Don't hide form automatically - let "Save & Add More" keep it open
    // Only hide if user clicked regular "Save" button (handled by form's onCancel)
  };

  // Fetch healthcare users for assignment
  const fetchHealthcareUsers = async () => {
    if (healthcareUsers.length > 0) return; // Already loaded
    try {
      setLoadingUsers(true);
      const response = await adminApi.getUsers({
        user_type: 'healthcare',
        is_active: 'true',
        health_system_id: fullReferral.health_system?.id
      });
      if (response.data?.users) {
        setHealthcareUsers(response.data.users);
      }
    } catch (err) {
      console.error('Failed to fetch healthcare users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle edit referral
  const handleStartEdit = () => {
    setEditFormData({
      case_name: fullReferral.case_name || '',
      assigned_to_id: fullReferral.assigned_to?.id || '',
      urgency_level: fullReferral.urgency_level || 'medium',
      overall_deadline: fullReferral.overall_deadline ? fullReferral.overall_deadline.split('T')[0] : '',
      case_description: fullReferral.case_description || ''
    });
    setIsEditingReferral(true);
    fetchHealthcareUsers();
  };

  const handleCancelEdit = () => {
    setIsEditingReferral(false);
    setEditFormData({});
  };

  const handleSaveEdit = async () => {
    try {
      // Clean up payload - remove empty assigned_to_id
      const payload = { ...editFormData };
      if (!payload.assigned_to_id) {
        delete payload.assigned_to_id;
      }

      const response = await adminApi.patch(`admin/referrals/${fullReferral.id}/`, payload);

      if (response.data?.success) {
        setAlertState({
          show: true,
          type: 'success',
          title: 'Referral Updated',
          message: 'Successfully updated referral details'
        });

        // Refresh referral details
        const refreshResponse = await adminApi.get(`admin/referrals/${fullReferral.id}/`);
        if (refreshResponse.data?.referral) {
          setFullReferral(refreshResponse.data.referral);
          dispatch(setSelectedReferral(refreshResponse.data.referral));
        }

        setIsEditingReferral(false);
        setEditFormData({});

        // Notify parent to refresh list
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.errors || err.response?.data?.error || 'Failed to update referral';
      setAlertState({
        show: true,
        type: 'error',
        title: 'Update Failed',
        message: typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg
      });
    }
  };

  // Handle edit patient
  const handleStartPatientEdit = () => {
    const patient = fullReferral.patient || {};
    const mrnInfo = getMrnInfo(patient);
    setPatientEditFormData({
      first_name: patient.first_name || '',
      last_name: patient.last_name || '',
      dob: patient.dob ? patient.dob.split('T')[0] : '',
      phone: patient.phone || '',
      email: patient.email || '',
      mrn: mrnInfo.value,
      mrn_editable: mrnInfo.editable,
      mrn_ehr_profile_id: mrnInfo.ehrProfileId,
      mrn_mode: mrnInfo.mode
    });
    setIsEditingPatient(true);
  };

  const handleCancelPatientEdit = () => {
    setIsEditingPatient(false);
    setPatientEditFormData({});
  };

  const handleSavePatientEdit = async () => {
    try {
      const patientId = fullReferral.patient?.id;
      if (!patientId) {
        setAlertState({
          show: true,
          type: 'error',
          title: 'Update Failed',
          message: 'Patient ID not found'
        });
        return;
      }

      // Separate MRN data from patient data
      // eslint-disable-next-line no-unused-vars
      const { mrn, mrn_editable, mrn_ehr_profile_id, mrn_mode, ...patientData } = patientEditFormData;
      const originalMrnInfo = getMrnInfo(fullReferral.patient);
      const mrnChanged = mrn !== originalMrnInfo.value;

      // Update patient data first
      const response = await adminApi.patch(`admin/patients/${patientId}/`, patientData);

      let finalPatient = response.data.patient;

      // If MRN is editable and changed, update it separately
      if (mrn_editable && mrnChanged && mrn_ehr_profile_id) {
        try {
          const mrnResponse = await adminApi.patch(`admin/patients/${patientId}/mrn/`, {
            ehr_profile_id: mrn_ehr_profile_id,
            mrn: mrn
          });
          // Use MRN update response if successful
          if (mrnResponse.data?.patient) {
            finalPatient = mrnResponse.data.patient;
          }
        } catch (mrnErr) {
          console.error('MRN update failed:', mrnErr);
          // Continue with patient update, show warning about MRN
          setAlertState({
            show: true,
            type: 'warning',
            title: 'Partial Update',
            message: 'Patient updated but MRN update failed: ' + (mrnErr.response?.data?.error || 'Unknown error')
          });
        }
      }

      if (response.data?.success || finalPatient) {
        // Update local state immediately with the updated patient data from response
        const updatedReferral = {
          ...fullReferral,
          patient: finalPatient
        };

        setFullReferral(updatedReferral);
        dispatch(setSelectedReferral(updatedReferral));

        // Only show success if no MRN warning was shown
        if (!mrn_editable || !mrnChanged || !mrn_ehr_profile_id) {
          setAlertState({
            show: true,
            type: 'success',
            title: 'Patient Updated',
            message: 'Successfully updated patient details'
          });
        } else {
          setAlertState({
            show: true,
            type: 'success',
            title: 'Patient Updated',
            message: 'Successfully updated patient details including MRN'
          });
        }

        setIsEditingPatient(false);
        setPatientEditFormData({});

        // Notify parent to refresh list
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.errors || err.response?.data?.error || 'Failed to update patient';
      setAlertState({
        show: true,
        type: 'error',
        title: 'Update Failed',
        message: typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg
      });
    }
  };

  // Handle EHR completion
  const handleSaveEhrCompletion = async () => {

    try {
      const payload = {
        ehr_completion: 'completed',
        ehr_metadata: {
          completion_reason: ehrCompletionReason.trim()
        }
      };

      const response = await adminApi.patch(`admin/referrals/${fullReferral.id}/`, payload);

      if (response.data?.success) {
        setAlertState({
          show: true,
          type: 'success',
          title: 'EHR Status Updated',
          message: 'Successfully marked EHR documentation as complete'
        });

        // Refresh referral details
        const refreshResponse = await adminApi.get(`admin/referrals/${fullReferral.id}/`);
        if (refreshResponse.data?.referral) {
          setFullReferral(refreshResponse.data.referral);
          dispatch(setSelectedReferral(refreshResponse.data.referral));
        }

        setIsEditingEhr(false);
        setEhrCompletionReason('');

        // Notify parent to refresh list
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.errors || err.response?.data?.error || 'Failed to update EHR status';
      setAlertState({
        show: true,
        type: 'error',
        title: 'Update Failed',
        message: typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg
      });
    }
  };

  const handleCancelEhrEdit = () => {
    setIsEditingEhr(false);
    setEhrCompletionReason('');
  };

  // Debounced facility search for edit form
  const searchEditFacilities = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setEditFacilitySearchResults([]);
        return;
      }

      setEditFacilitySearching(true);
      try {
        const response = await adminApi.get('admin/organizations/search/', {
          params: {
            search: searchTerm,
            referral_id: fullReferral?.id,
            page_size: 10
          }
        });

        if (response.data?.success) {
          setEditFacilitySearchResults(response.data.organizations || []);
        }
      } catch (err) {
        console.error('Failed to search facilities:', err);
      } finally {
        setEditFacilitySearching(false);
      }
    }, 300),
    [fullReferral?.id]
  );

  // Debounced contact search for edit form
  const searchEditContacts = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setEditContactSearchResults([]);
        return;
      }

      setEditContactSearching(true);
      try {
        const response = await adminApi.get('admin/prospects/search/', {
          params: {
            search: searchTerm,
            referral_id: fullReferral?.id,
            page_size: 10
          }
        });

        if (response.data?.success) {
          setEditContactSearchResults(response.data.prospects || []);
        }
      } catch (err) {
        console.error('Failed to search contacts:', err);
      } finally {
        setEditContactSearching(false);
      }
    }, 300),
    [fullReferral?.id]
  );

  // Handle item editing
  const handleStartItemEdit = (item) => {
    const itemData = {
      status_id: item.status?.id || '',
      facility_name: item.facility?.full_name || item.item_metadata?.facility_name || '',
      contact_name: (() => {
        const primaryContact = item.contact_relations?.find(rel => rel.is_primary) || item.contact_relations?.[0];
        return primaryContact?.contact?.full_name || item.item_metadata?.contact_name || '';
      })(),
      contact_phone: item.item_metadata?.contact_phone || '',
      contact_email: item.item_metadata?.contact_email || '',
      deadline: item.deadline ? item.deadline.split('T')[0] : '',
      item_notes: item.item_notes || ''
    };
    setItemEditData(itemData);
    setOriginalItemData(itemData);
    setEditingItemId(item.id);

    // Initialize search state with existing facility/contact
    if (item.facility) {
      setEditSelectedFacility(item.facility);
      setEditFacilitySearchTerm('');
    } else {
      setEditSelectedFacility(null);
      setEditFacilitySearchTerm(item.item_metadata?.facility_name || '');
    }

    const primaryContact = item.contact_relations?.find(rel => rel.is_primary) || item.contact_relations?.[0];
    if (primaryContact?.contact) {
      setEditSelectedContact(primaryContact.contact);
      setEditContactSearchTerm('');
    } else {
      setEditSelectedContact(null);
      setEditContactSearchTerm(item.item_metadata?.contact_name || '');
    }

    setEditFacilitySearchResults([]);
    setEditContactSearchResults([]);
  };

  const handleCancelItemEdit = () => {
    setEditingItemId(null);
    setItemEditData({});
    setOriginalItemData({});
    setEditSelectedFacility(null);
    setEditFacilitySearchTerm('');
    setEditFacilitySearchResults([]);
    setEditSelectedContact(null);
    setEditContactSearchTerm('');
    setEditContactSearchResults([]);
  };

  const handleToggleTaskActivity = (taskId) => {
    setExpandedTaskActivities(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const handleSaveItem = async (itemId) => {
    try {
      // Get current item for comparison
      const currentItem = fullReferral.items.find(i => i.id === itemId);
      if (!currentItem) {
        throw new Error('Task not found');
      }

      // Check if selected status is a completion status
      const selectedStatus = referralStatuses?.find(s => s.id === itemEditData.status_id);

      // Build payload with only changed fields
      const payload = {};

      // Only include status_id if changed
      if (itemEditData.status_id !== originalItemData.status_id) {
        payload.status_id = itemEditData.status_id;
      }

      // Only include deadline if changed
      if (itemEditData.deadline !== originalItemData.deadline) {
        payload.deadline = itemEditData.deadline;
      }

      // Only include notes if changed
      if (itemEditData.item_notes !== originalItemData.item_notes) {
        payload.item_notes = itemEditData.item_notes;
      }

      // FACILITY HANDLING - send facility_id or facility_data to create/link Prospect
      if (editSelectedFacility) {
        // User selected existing facility from dropdown
        if (editSelectedFacility.id !== currentItem.facility?.id) {
          payload.facility_id = editSelectedFacility.id;
        }
      } else if (editFacilitySearchTerm && editFacilitySearchTerm.trim()) {
        // User typed new facility name (not selected from dropdown)
        if (editFacilitySearchTerm.trim() !== currentItem.facility?.full_name) {
          payload.facility_data = {
            name: editFacilitySearchTerm.trim()
          };
        }
      }

      // CONTACT HANDLING - send contact_id or contact_data to create/link Prospect
      const currentContact = currentItem.contact_relations?.find(r => r.is_primary)?.contact;
      if (editSelectedContact) {
        // User selected existing contact from dropdown
        if (editSelectedContact.id !== currentContact?.id) {
          payload.contact_id = editSelectedContact.id;
        }
      } else if (editContactSearchTerm && editContactSearchTerm.trim()) {
        // User typed new contact name (not selected from dropdown)
        if (editContactSearchTerm.trim() !== currentContact?.full_name) {
          payload.contact_data = {
            full_name: editContactSearchTerm.trim(),
            phone: itemEditData.contact_phone || '',
            email: itemEditData.contact_email || '',
            is_primary: true,
            role: 'Facility Contact'
          };
        }
      }

      // Build metadata object for reference/display only
      // Note: Metadata does NOT create/update Prospect records
      const hasMetadataChanges =
        itemEditData.facility_name !== originalItemData.facility_name ||
        itemEditData.contact_name !== originalItemData.contact_name ||
        itemEditData.contact_phone !== originalItemData.contact_phone ||
        itemEditData.contact_email !== originalItemData.contact_email;

      if (hasMetadataChanges) {
        const metadata = {};
        if (itemEditData.facility_name) metadata.facility_name = itemEditData.facility_name;
        if (itemEditData.contact_name) metadata.contact_name = itemEditData.contact_name;
        if (itemEditData.contact_phone) metadata.contact_phone = itemEditData.contact_phone;
        if (itemEditData.contact_email) metadata.contact_email = itemEditData.contact_email;

        if (Object.keys(metadata).length > 0) {
          payload.item_metadata = metadata;
        }
      }

      // Add is_complete flag if status is a completion status
      if (selectedStatus?.is_complete) {
        payload.is_complete = true;
      }

      const response = await adminApi.patch(`admin/referral-items/${itemId}/`, payload);

      if (response.data?.success) {
        setAlertState({
          show: true,
          type: 'success',
          title: 'Task Updated',
          message: 'Successfully updated referral task'
        });

        // Refresh referral details
        const refreshResponse = await adminApi.get(`admin/referrals/${fullReferral.id}/`);
        if (refreshResponse.data?.referral) {
          setFullReferral(refreshResponse.data.referral);
          dispatch(setSelectedReferral(refreshResponse.data.referral));
        }

        setEditingItemId(null);
        setItemEditData({});
        setOriginalItemData({});

        // Trigger refresh of task activity timeline if expanded
        if (expandedTaskActivities[itemId]) {
          setTaskActivityRefreshTrigger(prev => ({
            ...prev,
            [itemId]: (prev[itemId] || 0) + 1
          }));
        }

        // Refresh activities to show the update
        fetchActivities();

        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (err) {
      console.error('Failed to update item:', err);
      const errorMsg = err.response?.data?.errors || err.response?.data?.error || 'Failed to update item';
      setAlertState({
        show: true,
        type: 'error',
        title: 'Update Failed',
        message: typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg
      });
    }
  };

  // ===== OUTREACH/CALLING FUNCTIONS (from RetellCallPanel) =====

  // Filter phone numbers based on selected agent
  const availablePhoneNumbers = useMemo(() => {
    if (!selectedAgentId || phoneNumbers.length === 0) {
      return phoneNumbers;
    }
    return phoneNumbers.filter(phone => {
      if (phone.outbound_agent_id === selectedAgentId) return true;
      if (phone.outbound_agents && Array.isArray(phone.outbound_agents)) {
        return phone.outbound_agents.some(agent => agent.agent_id === selectedAgentId);
      }
      if (phone.inbound_agent_id === selectedAgentId) return true;
      if (phone.inbound_agents && Array.isArray(phone.inbound_agents)) {
        return phone.inbound_agents.some(agent => agent.agent_id === selectedAgentId);
      }
      return false;
    });
  }, [selectedAgentId, phoneNumbers]);

  // Load Retell agents
  const loadAgents = useCallback(async () => {
    try {
      setLoadingAgents(true);
      const response = await adminApi.getRetellAgents();
      if (response.data && response.data.success && response.data.data) {
        const uniqueAgents = [];
        const seenIds = new Set();
        for (const agent of response.data.data) {
          if (!seenIds.has(agent.agent_id)) {
            uniqueAgents.push(agent);
            seenIds.add(agent.agent_id);
          }
        }
        setAgents(uniqueAgents);
        if (uniqueAgents.length > 0) {
          setSelectedAgentId(uniqueAgents[0].agent_id);
        }
      }
    } catch (err) {
      setAlertState({ show: true, type: 'danger', title: '', message: err.response?.data?.error || 'Failed to load agents' });
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  // Load Retell phone numbers
  const loadPhoneNumbers = useCallback(async () => {
    try {
      setLoadingPhoneNumbers(true);
      const response = await adminApi.getRetellPhoneNumbers();
      if (response.data && response.data.success && response.data.data) {
        setPhoneNumbers(response.data.data);
      }
    } catch (err) {
      // Silent fail for phone numbers
    } finally {
      setLoadingPhoneNumbers(false);
    }
  }, []);

  // Load outreach history
  const loadOutreachHistory = useCallback(async () => {
    if (!fullReferral?.id) return;
    try {
      setLoadingOutreachHistory(true);
      const response = await adminApi.getOutreachActivities({
        entity_id: fullReferral.id,
        entity_type: 'referral'
      });
      if (response.data) {
        setOutreachHistory(response.data);
      } else {
        setOutreachHistory([]);
      }
    } catch (err) {
      setOutreachHistory([]);
    } finally {
      setLoadingOutreachHistory(false);
    }
  }, [fullReferral?.id]);

  // Poll for queue status
  const pollQueueStatus = useCallback(async (queueId) => {
    try {
      const response = await adminApi.getCallQueueItem(queueId);
      if (response.data && response.data.success) {
        const queueData = response.data.call;
        let actualCallStatus = queueData.status;
        const retellCallStatus = queueData.retell_response?.call_status;

        if (queueData.status === 'completed' && queueData.retell_call_id && retellCallStatus) {
          if (retellCallStatus === 'not_connected' || retellCallStatus === 'error') {
            actualCallStatus = 'failed';
          } else if (retellCallStatus === 'ongoing' || retellCallStatus === 'registered') {
            actualCallStatus = 'in_progress';
          } else if (retellCallStatus === 'ended' || retellCallStatus === 'analyzed') {
            actualCallStatus = 'completed';
          } else {
            actualCallStatus = retellCallStatus;
          }
        }

        setCallStatus({
          call_status: actualCallStatus,
          queue_position: queueData.queue_position,
          queue_id: queueId,
          retell_call_id: queueData.retell_call_id,
          started_at: queueData.started_at,
          completed_at: queueData.completed_at,
          error_message: queueData.error_message,
          retell_response: queueData.retell_response,
          disconnection_reason: queueData.retell_response?.disconnection_reason
        });

        if (queueData.status === 'completed' || queueData.status === 'failed' || queueData.status === 'cancelled') {
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          await loadOutreachHistory();
          if (actualCallStatus === 'failed') {
            const errorMsg = queueData.retell_response?.message || queueData.error_message || 'Unknown error';
            setAlertState({ show: true, type: 'danger', title: 'Call Failed', message: errorMsg });
          }
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        setCallStatus(null);
        setCallId(null);
        await loadOutreachHistory();
        setAlertState({ show: true, type: 'danger', title: '', message: 'Call removed from queue' });
      } else {
        setAlertState({ show: true, type: 'danger', title: '', message: `Error getting queue status: ${err.response?.data?.message || err.message}` });
      }
    }
  }, [pollingInterval, loadOutreachHistory]);

  // Initiate call
  const handleInitiateCall = async () => {
    if (!phoneNumber) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Phone number is required' });
      return;
    }
    if (!fromNumber) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Agent phone number is required' });
      return;
    }
    if (!selectedAgentId) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Please select an agent' });
      return;
    }

    const toNumberE164 = toE164(phoneNumber);
    const fromNumberE164 = toE164(fromNumber);

    if (!isValidE164(toNumberE164)) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Invalid phone number' });
      return;
    }
    if (!isValidE164(fromNumberE164)) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Invalid agent phone number' });
      return;
    }

    try {
      setLoadingCall(true);
      setAlertState({ show: false, type: '', title: '', message: '' });

      let contextWithExtension = additionalContext;
      if (phoneExtension) {
        const extensionNote = `\n\nIMPORTANT: When the call connects, ask for extension ${phoneExtension}.`;
        contextWithExtension = additionalContext ? `${additionalContext}${extensionNote}` : extensionNote.trim();
      }

      const toNumberWithExtension = phoneExtension ? `${toNumberE164};${phoneExtension}` : toNumberE164;

      // Build referral-specific context
      const patientName = fullReferral.patient ? `${fullReferral.patient.first_name || ''} ${fullReferral.patient.last_name || ''}`.trim() : '';
      const referralContext = `Referral case: ${fullReferral.case_name || 'N/A'}. Patient: ${patientName}. ${fullReferral.case_description || ''}`;
      const patientMRN = fullReferral.patient_mrn || '';

      const callData = {
        to_number: toNumberWithExtension,
        from_number: fromNumberE164,
        entity_type: 'referral',
        entity_id: fullReferral.id,
        referral_id: fullReferral.id,
        prospect_name: prospectName || patientName,
        company_name: companyName || fullReferral.organization?.name || '',
        patient_mrn: patientMRN,
        override_agent_id: selectedAgentId,
        additional_context: `${referralContext}\n\n${contextWithExtension}`
      };

      const response = await adminApi.initiateRetellCall(callData);

      if (response.data && response.data.success) {
        const queueId = response.data.queue_id;
        const queueData = response.data.call;

        setCallId(queueId);
        setCallStatus({
          call_status: queueData?.status || 'queued',
          queue_position: response.data.queue_position,
          estimated_dial_time: response.data.estimated_dial_time,
          queue_id: queueId
        });

        await loadOutreachHistory();

        setAlertState({
          show: true,
          type: 'success',
          title: 'Call Queued',
          message: `Call to ${phoneNumber} added to queue (Position: ${response.data.queue_position})`
        });

        setShowCallSetup(false);

        // Polling is now controlled by autoRefreshCallQueue toggle
        // Initial status check
        pollQueueStatus(queueId);
      } else {
        const errorDetails = response.data?.details;
        let errorMessage = response.data?.error || 'Failed to initiate call';
        if (errorDetails?.message && errorDetails.message.includes('not found from phone-number')) {
          errorMessage = `Agent is not allowed to use phone number ${fromNumber}. Try a different number or contact Admin.`;
        } else if (errorDetails?.message) {
          errorMessage = `${errorMessage}: ${errorDetails.message}`;
        }
        setAlertState({ show: true, type: 'danger', title: '', message: errorMessage });
      }
    } catch (err) {
      let errorMessage = 'Failed to start call';
      const errorData = err.response?.data;
      if (errorData?.details?.message && errorData.details.message.includes('not found from phone-number')) {
        const phoneMatch = errorData.details.message.match(/Item ([+\d]+) not found/);
        const phoneNum = phoneMatch ? phoneMatch[1] : fromNumber;
        errorMessage = `Agent is not allowed to use phone number: ${phoneNum}`;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
        if (errorData.details?.message) {
          errorMessage += `: ${errorData.details.message}`;
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      setAlertState({ show: true, type: 'danger', title: '', message: errorMessage });
    } finally {
      setLoadingCall(false);
    }
  };

  // Handle viewing a call
  const handleViewCall = (callId) => {
    setSelectedCallId(callId);
    setViewerOpen(true);
  };

  // Toggle activity detail expansion
  const handleToggleActivityDetail = async (activityId) => {
    const isExpanding = !expandedActivities[activityId];
    setExpandedActivities(prev => ({ ...prev, [activityId]: isExpanding }));

    if (isExpanding && !activityDetails[activityId]) {
      try {
        setLoadingActivityDetails(prev => ({ ...prev, [activityId]: true }));
        const response = await adminApi.getOutreachActivity(activityId);
        setActivityDetails(prev => ({ ...prev, [activityId]: response.data }));
      } catch (err) {
        setAlertState({
          show: true,
          type: 'warning',
          title: '',
          message: `Error fetching activity details: ${err.response?.data?.error || err.message}`
        });
      } finally {
        setLoadingActivityDetails(prev => ({ ...prev, [activityId]: false }));
      }
    }
  };

  // Refresh single outreach item
  const handleRefreshSingleItem = async (entry) => {
    const activityId = entry.id;
    const callId = entry.retell_data?.call_id;
    const queueId = entry.retell_data?.queue_id;
    const isQueuedCall = queueId && !callId;

    try {
      setRefreshingCallId(queueId || callId);
      setAlertState({ show: false, type: '', title: '', message: '' });

      if (isQueuedCall) {
        try {
          const queueResponse = await adminApi.getCallQueueItem(queueId);
          const queueData = queueResponse.data?.call;
          let actualCallStatus = queueData.status;
          const retellCallStatus = queueData.retell_response?.call_status;

          if (queueData.status === 'completed' && queueData.retell_call_id && retellCallStatus) {
            if (retellCallStatus === 'not_connected' || retellCallStatus === 'error') {
              actualCallStatus = 'failed';
            } else if (retellCallStatus === 'ongoing' || retellCallStatus === 'registered') {
              actualCallStatus = 'in_progress';
            } else if (retellCallStatus === 'ended' || retellCallStatus === 'analyzed') {
              actualCallStatus = 'completed';
            } else {
              actualCallStatus = retellCallStatus;
            }
          }

          setOutreachHistory(prev =>
            prev.map(item =>
              item.id === activityId
                ? {
                    ...item,
                    retell_data: {
                      ...item.retell_data,
                      call_id: queueData.retell_call_id || null,
                      call_status: actualCallStatus,
                      queue_position: queueData.queue_position,
                      error_message: queueData.error_message || null,
                      retell_response: queueData.retell_response || null,
                      disconnection_reason: queueData.retell_response?.disconnection_reason
                    }
                  }
                : item
            )
          );
        } catch (queueErr) {
          if (queueErr.response?.status === 404) {
            await loadOutreachHistory();
            setAlertState({ show: true, type: 'success', title: '', message: 'Call processed - history updated' });
            setTimeout(() => setAlertState({ show: false, type: '', title: '', message: '' }), 2000);
            return;
          }
          throw queueErr;
        }
      } else if (callId) {
        const activityResponse = await adminApi.getOutreachActivity(activityId);
        setOutreachHistory(prev =>
          prev.map(item => (item.id === activityId ? activityResponse.data : item))
        );
      }
      setAlertState({ show: true, type: 'success', title: '', message: 'Item refreshed' });
      setTimeout(() => setAlertState({ show: false, type: '', title: '', message: '' }), 2000);
    } catch (err) {
      if (err.response?.status === 404) {
        await loadOutreachHistory();
        setAlertState({ show: true, type: 'danger', title: '', message: 'Item no longer exists' });
      } else {
        setAlertState({ show: true, type: 'danger', title: '', message: err.response?.data?.error || 'Failed to refresh' });
      }
    } finally {
      setRefreshingCallId(null);
    }
  };

  // Save phone numbers to referral
  const handleSavePhoneNumbers = async (updatedPhoneNumbers) => {
    try {
      await adminApi.patch(`admin/referrals/${fullReferral.id}/`, { phone_numbers: updatedPhoneNumbers });

      // Refresh referral details
      const response = await adminApi.get(`admin/referrals/${fullReferral.id}/`);
      if (response.data?.referral) {
        setFullReferral(response.data.referral);
        dispatch(setSelectedReferral(response.data.referral));
      }

      setAlertState({ show: true, type: 'success', title: '', message: 'Phone numbers updated successfully' });
      setTimeout(() => {
        setAlertState({ show: false, type: '', title: '', message: '' });
      }, 3000);
    } catch (err) {
      setAlertState({ show: true, type: 'danger', title: '', message: err.response?.data?.message || 'Failed to update phone numbers' });
    }
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const badges = {
      'registered': 'bg-secondary',
      'ongoing': 'bg-primary',
      'in_progress': 'bg-primary',
      'ended': 'bg-success',
      'analyzed': 'bg-success',
      'not_connected': 'bg-danger',
      'error': 'bg-danger',
      'canceled': 'bg-warning',
      'pending': 'bg-secondary',
      'queued': 'bg-info',
      'scheduled': 'bg-info',
      'completed': 'bg-success',
      'failed': 'bg-danger',
      'cancelled': 'bg-warning'
    };
    return badges[status] || 'bg-secondary';
  };

  // ===== ITEM-SPECIFIC CALLING FUNCTIONS =====

  // Truncate text to word limit
  const truncateToWordLimit = (text, maxWords) => {
    if (!text) return '';
    const words = text.split(/\s+/).filter(w => w);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '... (truncated)';
  };

  // Build smart context from item details
  const buildItemContext = (item) => {
    const parts = [];

    // Referral type
    if (item.referral_type?.name) {
      parts.push(`Referral Type: ${item.referral_type.name}`);
    }

    // Facility (prefer FK, fallback to metadata)
    if (item.facility?.full_name || item.item_metadata?.facility_name) {
      parts.push(`Facility: ${item.facility?.full_name || item.item_metadata?.facility_name}`);
    }

    // Contact information (prefer FK relations, fallback to metadata)
    const primaryContact = item.contact_relations?.find(rel => rel.is_primary) || item.contact_relations?.[0];
    if (primaryContact?.contact?.full_name || item.item_metadata?.contact_name) {
      parts.push(`Contact Person: ${primaryContact?.contact?.full_name || item.item_metadata?.contact_name}`);
    }
    if (primaryContact?.contact?.email || item.item_metadata?.contact_email) {
      parts.push(`Contact Email: ${primaryContact?.contact?.email || item.item_metadata?.contact_email}`);
    }

    // Deadline (expiration date)
    if (item.deadline) {
      const deadlineDate = new Date(item.deadline).toLocaleDateString();
      parts.push(`Deadline: ${deadlineDate}`);
    }

    // Status
    if (item.status?.name) {
      parts.push(`Status: ${item.status.name}`);
    }

    // Running notes
    if (item.item_notes) {
      parts.push(`Notes: ${item.item_notes}`);
    }

    const context = parts.join('\n\n');

    // Enforce 200-word limit
    return truncateToWordLimit(context, 200);
  };

  // Handle "Call with Gina" button click - set up call UI for specific item
  const handleStartItemCallSetup = (item) => {
    // Close any other item's call UI
    setCallingItemId(item.id);

    // Lazy load agents and phone numbers
    loadAgents();
    loadPhoneNumbers();

    // Build and set auto-generated context
    const autoContext = buildItemContext(item);
    setItemAdditionalContext(autoContext);

    // Pre-select primary contact if available
    const primaryContact = item.contact_relations?.find(r => r.is_primary);
    if (primaryContact) {
      setSelectedContactForItem(primaryContact.contact);
      if (primaryContact.preferred_phone) {
        setItemPhoneNumber(primaryContact.preferred_phone.phone || '');
        setItemPhoneExtension(primaryContact.preferred_phone.extension || '');
      }
    } else if (item.contact_relations?.length > 0) {
      // If no primary, select first contact
      const firstContact = item.contact_relations[0];
      setSelectedContactForItem(firstContact.contact);
      if (firstContact.preferred_phone) {
        setItemPhoneNumber(firstContact.preferred_phone.phone || '');
        setItemPhoneExtension(firstContact.preferred_phone.extension || '');
      }
    } else if (item.item_metadata?.contact_phone) {
      // Fallback: Use metadata contact info if no contact_relations
      setItemPhoneNumber(item.item_metadata.contact_phone || '');
      setItemPhoneExtension('');
      // Create a virtual contact object for display purposes
      if (item.item_metadata.contact_name) {
        setSelectedContactForItem({
          id: 'metadata-contact',
          full_name: item.item_metadata.contact_name,
          phone_numbers: []
        });
      }
    } else if (item.referral_type?.default_phone_number) {
      // Final fallback: Use default phone from ReferralType
      setItemPhoneNumber(item.referral_type.default_phone_number);
      setItemPhoneExtension(item.referral_type.default_phone_extension || '');
    }
  };

  // Handle contact selection for item call
  const handleSelectItemContact = (contactId, item) => {
    const relation = item.contact_relations.find(r => r.contact.id === contactId);
    if (relation) {
      setSelectedContactForItem(relation.contact);
      if (relation.preferred_phone) {
        setItemPhoneNumber(relation.preferred_phone.phone || '');
        setItemPhoneExtension(relation.preferred_phone.extension || '');
      } else {
        setItemPhoneNumber('');
        setItemPhoneExtension('');
      }
    }
  };

  // Cancel item call setup
  const handleCancelItemCall = () => {
    setCallingItemId(null);
    setItemAdditionalContext('');
    setSelectedContactForItem(null);
    setItemPhoneNumber('');
    setItemPhoneExtension('');
  };

  // Initiate call with item-specific context
  const handleStartItemCall = async (item) => {
    if (!itemPhoneNumber) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Contact phone number is required' });
      return;
    }
    if (!fromNumber) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Agent phone number is required' });
      return;
    }
    if (!selectedAgentId) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Please select an agent' });
      return;
    }

    const toNumberE164 = toE164(itemPhoneNumber);
    const fromNumberE164 = toE164(fromNumber);

    if (!isValidE164(toNumberE164)) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Invalid contact phone number' });
      return;
    }
    if (!isValidE164(fromNumberE164)) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Invalid agent phone number' });
      return;
    }

    try {
      setLoadingCall(true);
      setAlertState({ show: false, type: '', title: '', message: '' });

      // Build extension note if present
      let contextWithExtension = itemAdditionalContext;
      if (itemPhoneExtension) {
        const extensionNote = `\n\nIMPORTANT: When the call connects, ask for extension ${itemPhoneExtension}.`;
        contextWithExtension = itemAdditionalContext ? `${itemAdditionalContext}${extensionNote}` : extensionNote.trim();
      }

      const toNumberWithExtension = itemPhoneExtension ? `${toNumberE164};${itemPhoneExtension}` : toNumberE164;

      // Build referral-specific context with patient details
      const patient = fullReferral.patient;
      const patientName = patient ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim() : '';
      const patientDOB = patient?.dob ? new Date(patient.dob).toLocaleDateString('en-US', { timeZone: 'UTC' }) : null;
      const mrnInfo = getMrnInfo(patient);
      const patientMRN = mrnInfo.value || null;

      let referralContext = `Referral case: ${fullReferral.case_name || 'N/A'}. Patient: ${patientName}`;
      if (patientDOB) {
        referralContext += `. Patient DOB: ${patientDOB}`;
      }
      if (patientMRN) {
        referralContext += `. Patient MRN: ${patientMRN}`;
      }
      referralContext += '.';

      const callData = {
        to_number: toNumberWithExtension,
        from_number: fromNumberE164,
        entity_type: 'referral',
        entity_id: fullReferral.id,
        referral_id: fullReferral.id,
        task_id: item.id,
        prospect_name: selectedContactForItem?.full_name ||
          (selectedContactForItem?.first_name || selectedContactForItem?.last_name
            ? `${selectedContactForItem.first_name || ''} ${selectedContactForItem.last_name || ''}`.trim()
            : item.item_metadata?.contact_name),
        company_name: item.item_metadata?.facility_name || item.facility?.full_name || fullReferral.organization?.name || '',
        override_agent_id: selectedAgentId,
        additional_context: `${referralContext}\n\n${contextWithExtension}`,
        metadata: {
          entity_type: 'referral',
          entity_id: fullReferral.id,
          referral_id: fullReferral.id,
          task_id: item.id,
          referral_item_id: item.id,
          referral_type: item.referral_type?.name,
          facility: item.item_metadata?.facility_name || item.facility?.full_name,
          contact_id: selectedContactForItem?.id,
          contact_name: selectedContactForItem?.full_name ||
            (selectedContactForItem?.first_name || selectedContactForItem?.last_name
              ? `${selectedContactForItem.first_name || ''} ${selectedContactForItem.last_name || ''}`.trim()
              : item.item_metadata?.contact_name),
          contact_email: item.item_metadata?.contact_email,
          patient_name: patientName,
          patient_dob: patient?.dob || null,
          patient_mrn: patientMRN
        }
      };

      const response = await adminApi.initiateRetellCall(callData);

      if (response.data && response.data.success) {
        const queueId = response.data.queue_id;

        setCallId(queueId);
        setCallStatus({
          call_status: response.data.call?.status || 'queued',
          queue_position: response.data.queue_position,
          estimated_dial_time: response.data.estimated_dial_time,
          queue_id: queueId
        });

        await loadOutreachHistory();

        // Auto-update item status to "In Progress" if not already in a complete state
        try {
          if (!item.is_complete) {
            const inProgressStatus = referralStatuses?.find(s =>
              s.slug === 'in-progress' || s.name.toLowerCase().includes('in progress')
            );

            if (inProgressStatus && item.status?.id !== inProgressStatus.id) {
              await adminApi.patch(`admin/referral-items/${item.id}/`, {
                status_id: inProgressStatus.id
              });

              // Refresh referral to show updated status
              const refreshResponse = await adminApi.get(`admin/referrals/${fullReferral.id}/`);
              if (refreshResponse.data?.referral) {
                setFullReferral(refreshResponse.data.referral);
                dispatch(setSelectedReferral(refreshResponse.data.referral));
              }

              // Refresh activities to show new call activity
              fetchActivities();

              if (onUpdate) {
                onUpdate();
              }
            }
          }
        } catch (err) {
          console.error('Failed to auto-update item status:', err);
          // Don't block the call flow if status update fails
        }

        setAlertState({
          show: true,
          type: 'success',
          title: 'Call Queued',
          message: `Call to ${selectedContactForItem?.full_name ||
            (selectedContactForItem?.first_name || selectedContactForItem?.last_name
              ? `${selectedContactForItem.first_name || ''} ${selectedContactForItem.last_name || ''}`.trim()
              : item.item_metadata?.contact_name) || itemPhoneNumber} added to queue (Position: ${response.data.queue_position})`
        });

        // Collapse the item call setup
        handleCancelItemCall();

        // Polling is now controlled by autoRefreshCallQueue toggle
        // Initial status check
        pollQueueStatus(queueId);
      } else {
        const errorDetails = response.data?.details;
        let errorMessage = response.data?.error || 'Failed to initiate call';
        if (errorDetails?.message && errorDetails.message.includes('not found from phone-number')) {
          errorMessage = `Agent is not allowed to use phone number ${fromNumber}. Try a different number or contact Admin.`;
        } else if (errorDetails?.message) {
          errorMessage = `${errorMessage}: ${errorDetails.message}`;
        }
        setAlertState({ show: true, type: 'danger', title: '', message: errorMessage });
      }
    } catch (err) {
      let errorMessage = 'Failed to start call';
      const errorData = err.response?.data;
      if (errorData?.details?.message && errorData.details.message.includes('not found from phone-number')) {
        const phoneMatch = errorData.details.message.match(/Item ([+\d]+) not found/);
        const phoneNum = phoneMatch ? phoneMatch[1] : fromNumber;
        errorMessage = `Agent is not allowed to use phone number: ${phoneNum}`;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
        if (errorData.details?.message) {
          errorMessage += `: ${errorData.details.message}`;
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      setAlertState({ show: true, type: 'danger', title: '', message: errorMessage });
    } finally {
      setLoadingCall(false);
    }
  };


  // Load outreach history when Outreach tab is opened
  useEffect(() => {
    if (activeTab === 'outreach' && fullReferral?.id) {
      loadOutreachHistory();

      // Initialize form with patient data
      const patientFirstName = fullReferral.patient?.first_name || '';
      const patientLastName = fullReferral.patient?.last_name || '';
      setProspectName(patientFirstName && patientLastName ? `${patientFirstName} ${patientLastName}` : '');
      setCompanyName(fullReferral.organization?.name || '');
    }
  }, [activeTab, fullReferral?.id, fullReferral?.patient?.first_name, fullReferral?.patient?.last_name, fullReferral?.organization?.name, loadOutreachHistory]);

  // Auto-refresh outreach history while on Outreach tab (if enabled)
  useEffect(() => {
    if (activeTab === 'outreach' && fullReferral?.id && autoRefreshEnabled) {
      const interval = setInterval(loadOutreachHistory, 5000);
      return () => clearInterval(interval);
    }
  }, [activeTab, fullReferral?.id, autoRefreshEnabled, loadOutreachHistory]);

  // Auto-set phone number if only one is available
  useEffect(() => {
    if (callId) return; // Skip during active call

    // First, validate current fromNumber (if set)
    if (fromNumber && availablePhoneNumbers.length > 0) {
      const isAvailable = availablePhoneNumbers.some(phone => phone.phone_number === fromNumber);
      if (!isAvailable) {
        setFromNumber('');
        return; // Exit early, let next render handle auto-set
      }
    }

    // Then, auto-set if exactly one number available and fromNumber not already set
    if (availablePhoneNumbers.length === 1 && !fromNumber) {
      setFromNumber(availablePhoneNumbers[0].phone_number);
    }
  }, [selectedAgentId, availablePhoneNumbers, fromNumber, callId]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Auto-poll call queue status when enabled
  useEffect(() => {
    if (callId && autoRefreshCallQueue) {
      const interval = setInterval(() => {
        pollQueueStatus(callId);
      }, 3000);
      setPollingInterval(interval);
      return () => {
        clearInterval(interval);
        setPollingInterval(null);
      };
    }
  }, [callId, autoRefreshCallQueue, pollQueueStatus]);

  // Fetch activity with full payload for webhook details
  const fetchActivityWithPayload = useCallback(async (activityId) => {
    if (!fullReferral?.id) return null;

    const response = await adminApi.get(`admin/referrals/${fullReferral.id}/activities/${activityId}/`, {
      params: { response_format: 'full' }
    });
    return response.data;
  }, [fullReferral?.id]);

  // Handle tab switching with URL update
  const handleTabSwitch = useCallback((newTab) => {
    setActiveTab(newTab);
    navigate(`${location.pathname}?tab=${newTab}`, { replace: true });
  }, [location.pathname, navigate]);

  if (loading || !fullReferral) {
    return (
      <SidebarPanel isOpen={true} onClose={onClose} title="Loading..." style={{ width: '800px', maxWidth: '95vw' }}>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading referral details...</span>
          </div>
        </div>
      </SidebarPanel>
    );
  }

  const patient = fullReferral.patient || {};
  const items = fullReferral.items || [];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'fa-info-circle' },
    { id: 'tasks', label: 'Referral Tasks', icon: 'fa-list', count: items.length },
    { id: 'activity', label: 'Activity', icon: 'fa-tasks' }
  ];

  const urgencyColor =
    fullReferral.urgency_level === 'critical' ? 'danger' :
    fullReferral.urgency_level === 'high' ? 'warning text-dark' :
    fullReferral.urgency_level === 'medium' ? 'info' : 'secondary';

  const renderOverviewTab = () => (
    <div className="p-3">
      {/* Alert Messages */}
      {alertState.show && (
        <AlertMessage
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          onDismiss={() => setAlertState({ ...alertState, show: false })}
          className="mb-3"
        />
      )}

      {/* Patient Info Card */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="card-title text-primary mb-0">
              {isEditingPatient ? 'Edit Patient Details' : `${patient.first_name} ${patient.last_name}`}
            </h6>
            <div className="d-flex align-items-center gap-2">
              {!isEditingPatient && (
                <>
                  <span className={`badge ${fullReferral.is_complete ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {fullReferral.is_complete ? 'COMPLETE' : 'IN PROGRESS'}
                  </span>
                  <span className={`badge bg-${urgencyColor}`}>
                    {(fullReferral.urgency_level || 'medium').toUpperCase()}
                  </span>
                  <button className="btn btn-sm btn-outline-primary" onClick={handleStartPatientEdit}>
                    <i className="fas fa-edit me-1"></i>
                    Edit
                  </button>
                </>
              )}
            </div>
          </div>

          {isEditingPatient ? (
            <>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">First Name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={patientEditFormData.first_name}
                    onChange={(e) => setPatientEditFormData({ ...patientEditFormData, first_name: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Last Name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={patientEditFormData.last_name}
                    onChange={(e) => setPatientEditFormData({ ...patientEditFormData, last_name: e.target.value })}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Date of Birth</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={patientEditFormData.dob}
                    onChange={(e) => setPatientEditFormData({ ...patientEditFormData, dob: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">MRN</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={patientEditFormData.mrn || ''}
                    onChange={(e) => setPatientEditFormData({ ...patientEditFormData, mrn: e.target.value })}
                    disabled={!patientEditFormData.mrn_editable}
                    placeholder={patientEditFormData.mrn_mode === 'multiple' ? 'Multiple EHR profiles. Editing disabled.' : 'Enter MRN'}
                  />
                  <small className="text-muted">
                    {patientEditFormData.mrn_mode === 'single' ? '' :
                     patientEditFormData.mrn_mode === 'multiple' ? 'Go to Patient Management to edit MRN' :
                     'Organization-specific'}
                  </small>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Phone</label>
                  <input
                    type="tel"
                    className="form-control form-control-sm"
                    value={patientEditFormData.phone}
                    onChange={(e) => setPatientEditFormData({ ...patientEditFormData, phone: e.target.value })}
                    placeholder="+1234567890"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Email</label>
                  <input
                    type="email"
                    className="form-control form-control-sm"
                    value={patientEditFormData.email}
                    onChange={(e) => setPatientEditFormData({ ...patientEditFormData, email: e.target.value })}
                    placeholder="patient@example.com"
                  />
                </div>
              </div>

              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-primary" onClick={handleSavePatientEdit}>
                  Save Changes
                </button>
                <button className="btn btn-sm btn-secondary" onClick={handleCancelPatientEdit}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div className="row">
              <div className="col-md-6">
                <p className="mb-2"><strong>DOB:</strong> {patient.dob ? new Date(patient.dob).toLocaleDateString(undefined, { timeZone: 'UTC' }) : 'N/A'}</p>
              </div>
              <div className="col-md-6">
                <p className="mb-2">
                  <strong>MRN:</strong> {getMrnInfo(patient).display}
                  {getMrnInfo(patient).mode === 'multiple' && (
                    <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7em' }}>
                      {getMrnInfo(patient).count} profiles
                    </span>
                  )}
                </p>
              </div>
              <div className="col-md-6">
                <p className="mb-2"><strong>Phone:</strong> {patient.phone || 'N/A'}</p>
              </div>
              <div className="col-md-6">
                <p className="mb-2"><strong>Email:</strong> {patient.email || 'N/A'}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Case Info Card */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="card-title text-primary mb-0">Case Details</h6>
            {!isEditingReferral && (
              <button className="btn btn-sm btn-outline-primary" onClick={handleStartEdit}>
                <i className="fas fa-edit me-1"></i>
                Edit
              </button>
            )}
          </div>

          {isEditingReferral ? (
            <>
              <div className="mb-3">
                <label className="form-label fw-bold">Case Name</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={editFormData.case_name}
                  onChange={(e) => setEditFormData({ ...editFormData, case_name: e.target.value })}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Assigned To</label>
                {loadingUsers ? (
                  <div className="text-center py-2">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    <small>Loading users...</small>
                  </div>
                ) : (
                  <select
                    className="form-select form-select-sm"
                    value={editFormData.assigned_to_id}
                    onChange={(e) => setEditFormData({ ...editFormData, assigned_to_id: e.target.value })}
                  >
                    <option value="">Unassigned</option>
                    {healthcareUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} {user.role?.name && `(${user.role.name})`}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Urgency Level</label>
                  <select
                    className="form-select form-select-sm"
                    value={editFormData.urgency_level}
                    onChange={(e) => setEditFormData({ ...editFormData, urgency_level: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Overall Deadline</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={editFormData.overall_deadline}
                    onChange={(e) => setEditFormData({ ...editFormData, overall_deadline: e.target.value })}
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Description</label>
                <textarea
                  className="form-control form-control-sm"
                  rows="3"
                  value={editFormData.case_description}
                  onChange={(e) => setEditFormData({ ...editFormData, case_description: e.target.value })}
                ></textarea>
              </div>

              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-primary" onClick={handleSaveEdit}>
                  Save Changes
                </button>
                <button className="btn btn-sm btn-secondary" onClick={handleCancelEdit}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              {fullReferral.case_name && (
                <p className="mb-2"><strong>Case:</strong> {fullReferral.case_name}</p>
              )}
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-2"><strong>Assigned To:</strong> {fullReferral.assigned_to?.user ? `${fullReferral.assigned_to.user.first_name} ${fullReferral.assigned_to.user.last_name}` : 'Unassigned'}</p>
                </div>
                <div className="col-md-6">
                  <p className="mb-2"><strong>Owner:</strong> {fullReferral.organization?.name || fullReferral.health_system?.name || 'N/A'}</p>
                </div>
                <div className="col-md-6">
                  <p className="mb-2">
                    <strong>Overall Deadline:</strong>{' '}
                    {fullReferral.overall_deadline ? new Date(fullReferral.overall_deadline).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
              </div>
              {fullReferral.case_description && (
                <div className="mt-2">
                  <strong>Description:</strong>
                  <p className="text-muted mt-1">{fullReferral.case_description}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Progress Card */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title text-primary mb-3">Progress Summary</h6>
          <div className="mb-2 d-flex justify-content-between">
            <span>Total Tasks:</span>
            <strong>{fullReferral.total_items || 0}</strong>
          </div>
          <div className="mb-2 d-flex justify-content-between">
            <span className="text-success">Completed:</span>
            <strong className="text-success">{fullReferral.completed_items || 0}</strong>
          </div>
          <div className="mb-3 d-flex justify-content-between">
            <span className="text-warning">Pending:</span>
            <strong className="text-warning">{fullReferral.pending_items || 0}</strong>
          </div>

          {fullReferral.total_items > 0 && (
            <>
              <div className="progress" style={{ height: '8px' }}>
                <div
                  className="progress-bar bg-success"
                  role="progressbar"
                  style={{ width: `${((fullReferral.completed_items || 0) / fullReferral.total_items) * 100}%` }}
                  aria-valuenow={fullReferral.completed_items || 0}
                  aria-valuemin="0"
                  aria-valuemax={fullReferral.total_items}>
                </div>
              </div>
              <div className="text-center text-muted small mt-1">
                {Math.round(((fullReferral.completed_items || 0) / fullReferral.total_items) * 100)}% Complete
              </div>
            </>
          )}
        </div>
      </div>

      {/* EHR Status Card */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="card-title text-primary mb-0">EHR Documentation Status</h6>
            <span className={`badge bg-${fullReferral.ehr_completion === 'completed' ? 'success' : 'secondary'}`}>
              {fullReferral.ehr_completion === 'completed' ? 'COMPLETED' : 'PENDING'}
            </span>
          </div>

          {fullReferral.ehr_completion === 'completed' ? (
            <>
              <div className="alert alert-success mb-3">
                <i className="fas fa-check-circle me-2"></i>
                EHR documentation has been marked as complete
              </div>
              <div className="mb-2">
                <strong>Completed At:</strong>{' '}
                {fullReferral.ehr_completed_at ? new Date(fullReferral.ehr_completed_at).toLocaleString() : 'N/A'}
              </div>
              <div className="mb-2">
                <strong>Completed By:</strong> {fullReferral.ehr_completed_by_name || 'N/A'}
              </div>
              {fullReferral.ehr_completion_reason && (
                <div className="mt-2">
                  <strong>Reason:</strong>
                  <p className="text-muted mt-1 mb-0">{fullReferral.ehr_completion_reason}</p>
                </div>
              )}
            </>
          ) : isEditingEhr ? (
            <>
              <div className="mb-3">
                <label className="form-label fw-bold">Completion Reason (Optional)</label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={ehrCompletionReason}
                  onChange={(e) => setEhrCompletionReason(e.target.value)}
                  placeholder="e.g., All patient records have been uploaded to Epic system..."
                />
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-success"
                  onClick={handleSaveEhrCompletion}
                >
                  Mark Complete
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleCancelEhrEdit}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-muted mb-3">
                Mark this referral&apos;s EHR documentation as complete once all records have been finalized in the system.
              </p>
              <button
                className="btn btn-success"
                onClick={() => setIsEditingEhr(true)}
              >
                <i className="fas fa-check me-2"></i>
                Mark EHR Complete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderItemsTab = () => (
    <div className="p-3">
      {/* Alert Messages */}
      {alertState.show && (
        <AlertMessage
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          onDismiss={() => setAlertState({ ...alertState, show: false })}
          className="mb-3"
        />
      )}

      {/* Live Call Viewer */}
      {viewerOpen && selectedCallId && (
        <div className="mb-3">
          <RetellCallViewer
            callId={selectedCallId}
            onClose={() => {
              setViewerOpen(false);
              setSelectedCallId(null);
            }}
          />
        </div>
      )}

      {/* Call Queue Panel */}
      {queuePanelOpen && (
        <div className="mb-3">
          <CallQueuePanel
            onClose={() => setQueuePanelOpen(false)}
            onViewCall={(callId) => {
              setSelectedCallId(callId);
              setViewerOpen(true);
            }}
            entityType="referral"
            entityId={fullReferral?.id}
          />
        </div>
      )}

      {/* Add Item Form (Inline) */}
      {showAddForm ? (
        <div className="mb-3">
          <AddReferralItemForm
            referral={fullReferral}
            referralTypes={referralTypes}
            referralStatuses={referralStatuses}
            onSuccess={handleItemAdded}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      ) : (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 text-muted">
            {items.length} {items.length === 1 ? 'Task' : 'Tasks'}
          </h6>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setQueuePanelOpen(!queuePanelOpen)}
              title="View Call Queue"
            >
              <i className="fas fa-phone-volume me-1"></i>
              {queuePanelOpen ? 'Hide' : 'View'} Call Queue
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => setShowAddForm(true)}
            >
              <i className="fas fa-plus me-1"></i>
              Add Task
            </button>
          </div>
        </div>
      )}

      {/* Existing Items List */}
      {items.length === 0 && !showAddForm ? (
        <div className="text-center text-muted py-5">
          <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
          <p>No referral tasks yet. Let&apos;s create one!</p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => setShowAddForm(true)}
          >
            <i className="fas fa-plus me-2"></i>
            Add First Task
          </button>
        </div>
      ) : (
        items.map((item) => (
          <div key={item.id} className="card mb-3 shadow-sm">
            <div className="card-body">
              {editingItemId === item.id ? (
                <>
                  {/* Edit Mode */}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">{item.referral_type?.name || 'Unknown Type'}</h6>
                    <button className="btn btn-sm btn-link text-muted p-0" onClick={handleCancelItemEdit}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold small">Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={itemEditData.status_id}
                      onChange={(e) => setItemEditData({ ...itemEditData, status_id: e.target.value })}
                    >
                      {referralStatuses?.map(status => (
                        <option key={status.id} value={status.id}>
                          {status.name}{status.is_complete ? ' ✓' : ''}
                        </option>
                      ))}
                    </select>
                    {referralStatuses?.find(s => s.id === itemEditData.status_id)?.is_complete && (
                      <small className="text-success d-block mt-1">
                        <i className="fas fa-check-circle me-1"></i>
                        This status will mark the item as complete
                      </small>
                    )}
                  </div>

                  {/* Facility Name - Auto-search */}
                  <div className="mb-3">
                    <label className="form-label fw-bold small">Facility/Clinic Name</label>

                    {editSelectedFacility && (
                      <div className="alert alert-success alert-sm d-flex justify-content-between align-items-center mb-2 py-1">
                        <small><i className="fas fa-building me-2"></i>{editSelectedFacility.full_name}</small>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger py-0 px-1"
                          onClick={() => {
                            setEditSelectedFacility(null);
                            setEditFacilitySearchTerm('');
                            setEditFacilitySearchResults([]);
                            setItemEditData({ ...itemEditData, facility_name: '' });
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}

                    <div className="position-relative">
                      {!editSelectedFacility && (<input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Start typing to search or create new..."
                        value={editSelectedFacility ? editSelectedFacility.full_name : editFacilitySearchTerm}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEditFacilitySearchTerm(value);
                          setItemEditData({ ...itemEditData, facility_name: value });
                          searchEditFacilities(value);
                        }}
                        disabled={editSelectedFacility !== null}
                      />)}
                      {editFacilitySearching && (
                        <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      )}
                      {!editSelectedFacility && editFacilitySearchResults.length > 0 && (
                        <div className="list-group mt-1 position-absolute w-100" style={{zIndex: 1000, boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                          {editFacilitySearchResults.map(facility => (
                            <button
                              key={facility.id}
                              type="button"
                              className="list-group-item list-group-item-action"
                              onClick={() => {
                                setEditSelectedFacility(facility);
                                setItemEditData({ ...itemEditData, facility_name: facility.full_name });
                                setEditFacilitySearchResults([]);
                              }}
                            >
                              <i className="fas fa-building me-2"></i>{facility.full_name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {!editSelectedFacility && editFacilitySearchTerm && editFacilitySearchResults.length === 0 && !editFacilitySearching && (
                      <small className="text-muted d-block mt-1">
                        <i className="fas fa-info-circle me-1"></i>Will update to: &ldquo;{editFacilitySearchTerm}&rdquo;
                      </small>
                    )}
                  </div>

                  {/* Contact - Auto-search */}
                  <div className="mb-3">
                    <label className="form-label fw-bold small">Facility Contact (Optional)</label>

                    {editSelectedContact && (
                      <div className="alert alert-success alert-sm d-flex justify-content-between align-items-center mb-2 py-1">
                        <small>
                          <i className="fas fa-user me-2"></i>{editSelectedContact.full_name}
                          {editSelectedContact.phone_numbers && editSelectedContact.phone_numbers.length > 0 && (
                            <span className="text-muted ms-2">• {editSelectedContact.phone_numbers[0].phone}</span>
                          )}
                        </small>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger py-0 px-1"
                          onClick={() => {
                            setEditSelectedContact(null);
                            setEditContactSearchTerm('');
                            setEditContactSearchResults([]);
                            setItemEditData({ ...itemEditData, contact_name: '', contact_phone: '', contact_email: '' });
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}

                    <div className="position-relative mb-2">
                      {!editSelectedContact && (<input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Start typing to search or create new..."
                        value={editSelectedContact ? editSelectedContact.full_name : editContactSearchTerm}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEditContactSearchTerm(value);
                          setItemEditData({ ...itemEditData, contact_name: value });
                          searchEditContacts(value);
                        }}
                        disabled={editSelectedContact !== null}
                      />)}
                      {editContactSearching && (
                        <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      )}
                      {!editSelectedContact && editContactSearchResults.length > 0 && (
                        <div className="list-group mt-1 position-absolute w-100" style={{zIndex: 1000, boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                          {editContactSearchResults.map(contact => (
                            <button
                              key={contact.id}
                              type="button"
                              className="list-group-item list-group-item-action"
                              onClick={() => {
                                setEditSelectedContact(contact);
                                setItemEditData({
                                  ...itemEditData,
                                  contact_name: contact.full_name,
                                  contact_phone: contact.phone_numbers?.[0]?.phone || '',
                                  contact_email: contact.email || ''
                                });
                                setEditContactSearchResults([]);
                              }}
                            >
                              <i className="fas fa-user me-2"></i>{contact.full_name}
                              {contact.phone_numbers && contact.phone_numbers.length > 0 && (
                                <span className="text-muted ms-2">• {contact.phone_numbers[0].phone}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {!editSelectedContact && editContactSearchTerm && editContactSearchResults.length === 0 && !editContactSearching && (
                      <small className="text-muted d-block mb-2">
                        <i className="fas fa-info-circle me-1"></i>Will update to: &ldquo;{editContactSearchTerm}&rdquo;
                      </small>
                    )}

                    {/* Contact Phone & Email (optional fields) */}
                    {!editSelectedContact && (
                      <div className="row g-2">
                        <div className="col-md-6">
                          <input
                            type="tel"
                            className="form-control form-control-sm"
                            placeholder="Phone"
                            value={itemEditData.contact_phone || ''}
                            onChange={(e) => setItemEditData({ ...itemEditData, contact_phone: e.target.value })}
                          />
                        </div>
                        <div className="col-md-6">
                          <input
                            type="email"
                            className="form-control form-control-sm"
                            placeholder="Email"
                            value={itemEditData.contact_email || ''}
                            onChange={(e) => setItemEditData({ ...itemEditData, contact_email: e.target.value })}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold small">Deadline</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={itemEditData.deadline}
                      onChange={(e) => setItemEditData({ ...itemEditData, deadline: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold small">Notes</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows="2"
                      value={itemEditData.item_notes}
                      onChange={(e) => setItemEditData({ ...itemEditData, item_notes: e.target.value })}
                    ></textarea>
                  </div>

                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-primary" onClick={() => handleSaveItem(item.id)}>
                      Save
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={handleCancelItemEdit}>
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* View Mode */}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="mb-1">{item.referral_type?.name || 'Unknown Type'}</h6>
                      {(item.facility?.full_name || item.item_metadata?.facility_name) && (
                        <span className="text-primary d-block">
                          <i className="fas fa-building me-1"></i>
                          {item.facility?.full_name || item.item_metadata?.facility_name}
                        </span>
                      )}
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <span className={`badge ${item.is_complete ? 'bg-success' : 'bg-info'}`}>
                        {item.status?.name || 'Unknown'}
                      </span>
                      <button
                        className="btn btn-sm btn-generate-note btn-call"
                        onClick={() => handleStartItemCallSetup(item)}
                        title="Call with Gina AI"
                      >
                        {/* <i className="fas fa-phone me-1"></i> */}
                        Call
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleStartItemEdit(item)}
                        title="Edit item"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    </div>
                  </div>

                  {item.deadline && (
                    <p className="mb-2 text-muted small">
                      <i className="fas fa-calendar me-1"></i>
                      Deadline: {new Date(item.deadline).toLocaleDateString()}
                    </p>
                  )}

                  {item.item_notes && (
                    <div className="alert alert-light py-2 px-3 mb-2 small">
                      {item.item_notes}
                    </div>
                  )}

                  {/* Inline Call Setup Form */}
                  {callingItemId === item.id && (
                    <div className="border rounded p-3 mt-2 bg-light">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="text-primary mb-0">
                          <i className="fas fa-phone me-2"></i>
                          Call with Gina - {item.referral_type?.name}
                        </h6>
                        <button
                          className="btn btn-sm btn-link text-muted p-0"
                          onClick={handleCancelItemCall}
                          title="Close"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>

                      {/* Agent Selection */}
                      <div className="row mb-2">
                        <div className="col-6">
                          <label className="form-label fw-semibold small">AI Agent</label>
                          {loadingAgents ? (
                            <div className="text-center py-2">
                              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                              <small>Loading...</small>
                            </div>
                          ) : agents.length === 0 ? (
                            <div className="alert alert-warning py-1 mb-0 small">
                              <i className="fas fa-exclamation-circle me-1"></i>No agents found
                            </div>
                          ) : (
                            <select
                              className="form-select form-select-sm"
                              value={selectedAgentId}
                              onChange={(e) => setSelectedAgentId(e.target.value)}
                            >
                              {agents.map((agent) => (
                                <option key={agent.agent_id} value={agent.agent_id}>
                                  {agent.agent_name || agent.agent_id}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>

                        {/* Agent Phone - Read Only */}
                        <div className="col-6">
                          <label className="form-label fw-semibold small">Call From (Read-Only)</label>
                          {loadingPhoneNumbers ? (
                            <div className="text-center py-2">
                              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                              <small>Loading...</small>
                            </div>
                          ) : availablePhoneNumbers.length === 0 ? (
                            <input
                              type="tel"
                              className="form-control form-control-sm"
                              value={fromNumber}
                              disabled
                              placeholder="+1xxx-xxx-xxxx"
                            />
                          ) : availablePhoneNumbers.length === 1 ? (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={availablePhoneNumbers[0].phone_number_pretty || availablePhoneNumbers[0].phone_number}
                              disabled
                            />
                          ) : (
                            <select
                              className="form-select form-select-sm"
                              value={fromNumber}
                              onChange={(e) => setFromNumber(e.target.value)}
                            >
                              <option value="">Select phone...</option>
                              {availablePhoneNumbers.map((phone) => (
                                <option key={phone.phone_number} value={phone.phone_number}>
                                  {phone.phone_number_pretty || phone.phone_number}
                                  {phone.nickname ? ` - ${phone.nickname}` : ''}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>

                      {/* Contact Selection */}
                      {item.contact_relations && item.contact_relations.length > 0 && (
                        <div className="mb-2">
                          <label className="form-label fw-semibold small">Contact to Call</label>
                          <select
                            className="form-select form-select-sm"
                            value={selectedContactForItem?.id || ''}
                            onChange={(e) => handleSelectItemContact(e.target.value, item)}
                          >
                            <option value="">Select contact...</option>
                            {item.contact_relations.map(rel => (
                              <option key={rel.contact.id} value={rel.contact.id}>
                                {rel.contact.full_name}
                                {rel.role && ` (${rel.role})`}
                                {rel.is_primary && ' - Primary'}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Phone Number Manager */}
                      <div className="mb-2">
                        <label className="form-label fw-semibold small">Phone Number *</label>
                        <PhoneNumberManager
                          key={selectedContactForItem?.id || 'no-contact'}
                          phoneNumbers={selectedContactForItem?.phone_numbers || []}
                          onSave={() => {
                            // Optional: Implement phone number save if needed
                          }}
                          onChange={(e164Phone, extension) => {
                            setItemPhoneNumber(e164Phone);
                            setItemPhoneExtension(extension || '');
                          }}
                          disabled={false}
                        />
                      </div>

                      {/* Smart Context - PRE-POPULATED & EDITABLE */}
                      <div className="mb-2">
                        <label className="form-label fw-semibold small">
                          Context for Gina AI
                          <span className={`ms-2 ${
                            itemAdditionalContext.split(/\s+/).filter(w => w).length >= 200 ? 'text-danger' :
                            itemAdditionalContext.split(/\s+/).filter(w => w).length >= 180 ? 'text-warning' :
                            'text-muted'
                          }`}>
                            ({itemAdditionalContext.split(/\s+/).filter(w => w).length}/200 words)
                          </span>
                        </label>
                        <textarea
                          className="form-control form-control-sm"
                          rows="6"
                          value={itemAdditionalContext}
                          onChange={(e) => {
                            const words = e.target.value.split(/\s+/).filter(w => w);
                            if (words.length <= 200) {
                              setItemAdditionalContext(e.target.value);
                            }
                          }}
                          placeholder="Context auto-generated from item details. You can edit or remove any information before calling."
                          style={{ fontSize: '0.875rem' }}
                        />
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          This context will be shared with Gina during the call. Edit as needed.
                        </small>
                      </div>

                      {/* Action Buttons */}
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleStartItemCall(item)}
                          disabled={loadingCall || !selectedAgentId || !fromNumber || !itemPhoneNumber}
                        >
                          {loadingCall ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                              Initiating...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-rocket me-1"></i>
                              Start Call
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={handleCancelItemCall}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {item.contact_relations && item.contact_relations.length > 0 && (
                <div className="mt-3">
                  <small className="text-muted fw-bold d-block">Contacts:</small>
                  {item.contact_relations.map((relation) => (
                    <div key={relation.id} className="d-flex justify-content-between align-items-center py-2">
                      <div>
                        <i className="fas fa-user me-2 text-muted"></i>
                        {relation.contact?.full_name || 'Unknown'}
                        {relation.is_primary && (
                          <span className="badge bg-primary ms-2" style={{ fontSize: '0.7em' }}>Primary</span>
                        )}
                        {relation.role && (
                          <span className="text-muted small ms-2">- {relation.role}</span>
                        )}
                      </div>
                      {/* {relation.preferred_phone && (
                        <button
                          onClick={() => handleStartItemCallSetup(item)}
                          className="btn btn-sm btn-success"
                          title={`Call ${relation.preferred_phone.phone}`}
                        >
                          <i className="fas fa-phone me-1"></i>
                          Call
                        </button>
                      )} */}
                    </div>
                  ))}
                </div>
              )}

              {/* Task Activity Timeline */}
              <TaskActivityTimeline
                taskId={item.id}
                referralId={fullReferral.id}
                isExpanded={expandedTaskActivities[item.id] || false}
                onToggle={handleToggleTaskActivity}
                refreshTrigger={taskActivityRefreshTrigger[item.id] || 0}
                onViewCall={handleViewCall}
                isAnkrAdmin={isAnkrAdmin}
              />
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderOutreachTab = () => (
    <div className="p-3">
      {/* Alert Messages */}
      {alertState.show && (
        <AlertMessage
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          onDismiss={() => setAlertState({ ...alertState, show: false })}
          className="mb-3"
        />
      )}

      {/* Patient/Contact Info Card */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title text-primary mb-3">Contact Information</h6>
          <div className="mb-2">
            <strong>Patient:</strong> {patient.first_name} {patient.last_name}
          </div>
          {patient.phone && (
            <div className="mb-2">
              <strong>Phone:</strong> {patient.phone}
            </div>
          )}
          {fullReferral.organization && (
            <div className="mb-2">
              <strong>Organization:</strong> {fullReferral.organization.name}
            </div>
          )}
          {fullReferral.case_name && (
            <div className="mb-2">
              <strong>Case:</strong> {fullReferral.case_name}
            </div>
          )}
        </div>
      </div>

      {/* Call Setup */}
      <div className="card mb-3">
        <div
          className="card-header bg-white d-flex justify-content-between align-items-center border-0"
          style={{ cursor: 'pointer', borderRadius:'8px' }}
          onClick={() => { setShowCallSetup(!showCallSetup); if (!showCallSetup) { setCallId(null); setCallStatus(null); } }}
        >
          <h6 className="card-title text-primary border-0 mb-0">Setup Call</h6>
          <i className={`fas fa-chevron-${showCallSetup ? 'up' : 'down'} text-muted`}></i>
        </div>
        {showCallSetup && (
          <div className="card-body p-3">
            <div className="row mb-3">
              <div className="col-6 mb-2">
                <label className="form-label fw-semibold mb-1">Select AI Agent</label>
                {loadingAgents ? (
                  <div className="text-center py-2">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    <small>Loading agents...</small>
                  </div>
                ) : agents.length === 0 ? (
                  <div className="alert alert-warning py-2 mb-0">
                    <small><i className="fas fa-exclamation-circle me-2"></i>No agents found</small>
                  </div>
                ) : (
                  <select
                    className="form-select"
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    disabled={!!callId}
                  >
                    {agents.map((agent) => (
                      <option key={agent.agent_id} value={agent.agent_id}>
                        {agent.agent_name || agent.agent_id}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="col-6">
                <label className="form-label fw-semibold mb-1">Agent&apos;s Phone Number *</label>
                {loadingPhoneNumbers ? (
                  <div className="text-center py-2">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    <small>Loading phone numbers...</small>
                  </div>
                ) : availablePhoneNumbers.length === 0 ? (
                  <div>
                    <input
                      type="tel"
                      className="form-control"
                      value={fromNumber}
                      onChange={(e) => setFromNumber(e.target.value)}
                      placeholder="+1xxx-xxx-xxxx"
                      disabled={!!callId}
                      title="No phone numbers available for this agent. Enter manually."
                    />
                    {selectedAgentId && phoneNumbers.length > 0 && (
                      <small className="text-muted d-block mt-1">
                        <i className="fas fa-info-circle me-1"></i>
                        No phone numbers configured for this agent
                      </small>
                    )}
                  </div>
                ) : availablePhoneNumbers.length === 1 ? (
                  <div>
                    <input
                      type="text"
                      className="form-control"
                      value={availablePhoneNumbers[0].phone_number_pretty || availablePhoneNumbers[0].phone_number}
                      disabled
                      title="Only phone number available for this agent"
                    />
                    {availablePhoneNumbers[0].nickname && (
                      <small className="text-muted d-block mt-1">
                        <i className="fas fa-info-circle me-1"></i>
                        {availablePhoneNumbers[0].nickname}
                      </small>
                    )}
                  </div>
                ) : (
                  <select
                    className="form-select"
                    value={fromNumber}
                    onChange={(e) => setFromNumber(e.target.value)}
                    disabled={!!callId}
                    title="Select from available phone numbers for this agent"
                  >
                    <option value="">Select a phone number...</option>
                    {availablePhoneNumbers.map((phone) => (
                      <option key={phone.phone_number} value={phone.phone_number}>
                        {phone.phone_number_pretty || phone.phone_number} {phone.nickname ? `- ${phone.nickname}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-2">
              <div className="row g-2">
                <div className="col-6 mb-2">
                  <label className="form-label fw-semibold mb-1">Contact Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={prospectName}
                    onChange={(e) => setProspectName(e.target.value)}
                    placeholder="Patient or contact name"
                    disabled={!!callId}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold mb-1">Contact Phone Number *</label>
                  <PhoneNumberManager
                    key={fullReferral?.id}
                    phoneNumbers={fullReferral?.phone_numbers || []}
                    onSave={handleSavePhoneNumbers}
                    onChange={(e164Phone, extension) => {
                      setPhoneNumber(e164Phone);
                      setPhoneExtension(extension || '');
                    }}
                    disabled={!!callId}
                  />
                </div>
              </div>
            </div>

            {/* Call Context */}
            <div className="mb-2">
              <div className="mb-2">
                <label className="form-label fw-semibold mb-1">Organization/Facility Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Organization name to use during call"
                  disabled={!!callId}
                />
              </div>
              <div>
                <label className="form-label fw-semibold mb-1">Additional Context for AI Agent</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="Any specific notes, prior conversation details, etc..."
                  disabled={!!callId}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary flex-grow-1"
                onClick={handleInitiateCall}
                disabled={loadingCall || !fromNumber || !phoneNumber || !selectedAgentId}
              >
                {loadingCall ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Initiating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-rocket me-2"></i>
                    Start Call
                  </>
                )}
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={() => setQueuePanelOpen(true)}
                title="View Call Queue"
              >
                <i className="fas fa-list-ul"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Live Call Status */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="card-title text-primary mb-0">Live Call Status</h6>
            {callId && (
              <button
                className={`btn btn-sm ${autoRefreshCallQueue ? 'btn-outline-success' : 'btn-outline-secondary'} py-0 px-2`}
                onClick={() => setAutoRefreshCallQueue(!autoRefreshCallQueue)}
                title={autoRefreshCallQueue ? "Auto-polling enabled (every 3s)" : "Enable auto-polling"}
              >
                <i className={`fas fa-${autoRefreshCallQueue ? 'pause' : 'play'} me-1`}></i>
                {autoRefreshCallQueue ? 'Auto-poll On' : 'Auto-poll Off'}
              </button>
            )}
          </div>
          {callStatus ? (
            <>
              <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                <span className={`badge ${getStatusBadge(callStatus.call_status)}`}>
                  {callStatus.call_status || 'Unknown'}
                </span>
                {callStatus.queue_id && (
                  <code className="small" title="Queue ID">{callStatus.queue_id}</code>
                )}
                {callStatus.retell_call_id && (
                  <code className="small" title="Retell Call ID">{callStatus.retell_call_id}</code>
                )}
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setQueuePanelOpen(true)}
                  title="View Call Queue"
                >
                  <i className="fas fa-list-ul"></i>
                </button>
              </div>

              {callStatus.queue_position && (
                <div className="small text-muted mb-1">
                  <i className="fas fa-list-ol me-1"></i>
                  Queue Position: {callStatus.queue_position}
                </div>
              )}
              {callStatus.estimated_dial_time && (
                <div className="small text-muted mb-1">
                  <i className="fas fa-clock me-1"></i>
                  Estimated Dial: {new Date(callStatus.estimated_dial_time).toLocaleTimeString()}
                </div>
              )}
              {(callStatus.retell_response?.message || callStatus.error_message || callStatus.disconnection_reason) && (
                <div className="alert alert-danger py-2 mb-2">
                  <div className="d-flex align-items-start">
                    <i className="fas fa-exclamation-circle me-2 mt-1"></i>
                    <div>
                      <strong>
                        {callStatus.call_status === 'not_connected' ? 'Call Not Connected:' : 'Call Failed:'}
                      </strong>
                      <div className="mt-1">
                        {callStatus.retell_response?.message || callStatus.error_message ||
                         (callStatus.disconnection_reason && `Disconnected: ${callStatus.disconnection_reason}`)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted py-1">
              <p className="mb-0"><i className="fas fa-phone-slash mb-2"></i> No active call</p>
              <button
                className="btn btn-sm btn-outline-primary mt-2"
                onClick={() => setQueuePanelOpen(true)}
              >
                <i className="fas fa-list-ul me-1"></i>View Call Queue
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Outreach History */}
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="card-title text-primary mb-0">Outreach History</h6>
            <div className="d-flex gap-2">
              <button
                className={`btn btn-sm ${autoRefreshEnabled ? 'btn-outline-success' : 'btn-outline-secondary'} py-0 px-2`}
                onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                title={autoRefreshEnabled ? "Auto-refresh enabled (every 5s)" : "Enable auto-refresh"}
              >
                <i className={`fas fa-${autoRefreshEnabled ? 'pause' : 'play'} me-1`}></i>
                {autoRefreshEnabled ? 'Auto-refresh On' : 'Auto-refresh Off'}
              </button>
              <button
                className="btn btn-sm btn-outline-primary py-0 px-2"
                onClick={loadOutreachHistory}
                disabled={loadingOutreachHistory}
                title="Refresh outreach history"
              >
                {loadingOutreachHistory ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="fas fa-sync-alt"></i>
                )}
              </button>
            </div>
          </div>

          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            {loadingOutreachHistory ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Loading outreach history...
              </div>
            ) : (!outreachHistory || outreachHistory.length === 0) ? (
              <div className="text-muted text-center py-2">
                <i className="fas fa-history me-2"></i>No outreach history
              </div>
            ) : (
              outreachHistory.map((entry, index) => {
                const activityType = entry.activity_type?.name || 'Unknown';
                const activityDate = entry.activity_date;
                const callId = entry.retell_data?.call_id || entry.retell_data?.queue_id;
                const isQueueId = !entry.retell_data?.call_id && entry.retell_data?.queue_id;
                const callStatus = entry.retell_data?.call_status;
                const toNumber = entry.retell_data?.to_number;
                const queuePosition = entry.retell_data?.queue_position;
                const isLast = index === outreachHistory.length - 1;
                const hasEmailBody = entry.retell_data?.has_email_body;
                const isExpanded = expandedActivities[entry.id];
                const fullDetails = activityDetails[entry.id];
                const isLoadingDetails = loadingActivityDetails[entry.id];

                const activeQueueStatuses = ['pending', 'queued', 'scheduled', 'in_progress'];
                const isActivelyQueued = isQueueId && callStatus && activeQueueStatuses.includes(callStatus);

                return (
                  <div
                    key={entry.id || index}
                    className={`pb-2 mb-2 ${!isLast ? 'border-bottom' : ''}`}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <span className="badge badge-sm bg-info me-1">{activityType.toUpperCase()}</span>
                        {callStatus && (
                          <span className={`badge badge-sm ${getStatusBadge(callStatus)}`}>
                            {callStatus}
                          </span>
                        )}
                      </div>
                      <p className="text-muted mb-0 small">
                        {new Date(activityDate).toLocaleDateString()} {new Date(activityDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div>
                      {toNumber && <div className="text-muted small">{toNumber}</div>}
                      {queuePosition && <div className="text-muted small">Queue Position: {queuePosition}</div>}
                      {entry.notes && <div className="text-muted small">{entry.notes}</div>}

                      {hasEmailBody && (
                        <div className="mt-1">
                          <button
                            className="btn btn-sm btn-outline-secondary py-0 px-1"
                            onClick={() => handleToggleActivityDetail(entry.id)}
                            disabled={isLoadingDetails}
                            title={isExpanded ? "Hide details" : "View details"}
                          >
                            {isLoadingDetails ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                              <>
                                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} me-1`}></i>
                                {isExpanded ? 'Hide' : 'View'} Details
                              </>
                            )}
                          </button>
                        </div>
                      )}

                      {isExpanded && fullDetails && (
                        <div className="mt-2 p-2 border rounded bg-light small">
                          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                            {JSON.stringify(fullDetails, null, 2)}
                          </pre>
                        </div>
                      )}

                      {callId && (
                        <div className="d-flex justify-content-between align-items-center mt-1">
                          <code className="small" title={isQueueId ? "Queue ID" : "Call ID"}>
                            {isActivelyQueued && <i className="fas fa-hourglass-half me-1 text-muted"></i>}
                            {callId}
                          </code>
                          <div className="d-flex gap-2">
                            {isActivelyQueued && (
                              <span className="text-secondary me-2 small">
                                <i className="fas fa-clock me-1"></i>
                                {callStatus === 'in_progress' ? 'Processing' :
                                 callStatus === 'scheduled' ? 'Scheduled' :
                                 queuePosition ? `Queue #${queuePosition}` : 'In Queue'}
                              </span>
                            )}
                            <button
                              className="btn btn-sm btn-outline-secondary py-0 px-1"
                              onClick={() => handleRefreshSingleItem(entry)}
                              disabled={refreshingCallId === callId}
                              title="Refresh status"
                            >
                              {refreshingCallId === callId ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                <i className="fas fa-sync-alt"></i>
                              )}
                            </button>
                            {!isQueueId && (
                              <button
                                className="btn btn-sm btn-outline-primary py-0 px-1"
                                onClick={() => handleViewCall(callId)}
                                title="View call details"
                              >
                                <i className="fas fa-eye me-1"></i>View
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderActivityTab = () => (
    <div className="p-3">
      <ActivityTimeline
        activities={activitiesData}
        loading={activitiesLoading}
        onRefresh={fetchActivities}
        onFetchActivityWithPayload={fetchActivityWithPayload}
        showAutoRefresh={true}
        autoRefreshInterval={5000}
        onViewCall={handleViewCall}
        isAnkrAdmin={isAnkrAdmin}
      />
    </div>
  );

  // Render title with navigation buttons
  const renderTitle = () => {
    const title = `${patient.first_name} ${patient.last_name}`;

    // Only show navigation if we have the required props
    if (!onNavigate || currentIndex === undefined || totalEvents === undefined) {
      return title;
    }

    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < totalEvents - 1;

    return (
      <div className="d-flex align-items-center gap-2">
        <span className="flex-grow-1 me-2">{title}</span>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => handleNavigate('prev')}
          disabled={!hasPrev || isNavigating}
          title="Previous referral"
        >
          <i className="fas fa-chevron-left"></i>
        </button>
        <span className="text-muted small">
          {currentIndex + 1} of {totalEvents}
        </span>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => handleNavigate('next')}
          disabled={!hasNext || isNavigating}
          title="Next referral"
        >
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>
    );
  };

  return (
    <>
      <SidebarPanel
        isOpen={true}
        onClose={onClose}
        title={renderTitle()}
        style={{ width: '800px', maxWidth: '95vw' }}
      >
        {/* Tabs */}
        <ul className="nav nav-tabs px-3 pt-3" role="tablist">
          {tabs.map(tab => (
            <li key={tab.id} className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabSwitch(tab.id)}
                type="button"
                role="tab"
              >
                <i className={`fas ${tab.icon} me-1`}></i>
                {tab.label}
                {tab.count !== undefined && ` (${tab.count})`}
              </button>
            </li>
          ))}
        </ul>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'tasks' && renderItemsTab()}
          {activeTab === 'activity' && renderActivityTab()}
        </div>
      </SidebarPanel>

      {/* Call Viewer Modal */}
      <RetellCallViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        callId={selectedCallId}
      />

      {/* Call Queue Panel */}
      <CallQueuePanel
        isOpen={queuePanelOpen}
        onClose={() => setQueuePanelOpen(false)}
      />
    </>
  );
};

export default ReferralDetailPanel;
